import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button } from "rsuite";
import MegaphoneIcon from "../../../../assets/img/action-center/megaphone.svg";
import ClockIcon from "../../../../assets/img/action-center/clock.svg";
import ChevronRightWhiteIcon from "../../../../assets/img/action-center/chevron-right-white.svg";
import { getCampaignClientLogo } from "../../../../utils/clientLogos";
import useHeapCardViewed from "../../../../hooks/useHeapCardViewed";
import CampaignReviewModal from "../campaign-modal/CampaignReviewModal";
import CampaignActionForm from "../campaign-modal/CampaignActionForm";
import {
  BUYER_CAMPAIGN_BADGE,
  HERO_TITLE,
  BODY_PREFIX,
  BODY_SUFFIX,
  BODY_TAIL,
  EXPIRY_PREFIX,
  EXPIRY_SUFFIX,
  MAYBE_LATER_LABEL,
  LEARN_MORE_LABEL,
  BUYER_FALLBACK_LABEL,
} from "../campaignConstants";
import "./CampaignCard.scss";

const CampaignCard = ({
  data = [],
  onDismiss,
  selectedProperty,
  cardPosition,
  hotelName,
}) => {
  const cardRef = useHeapCardViewed("campaign", cardPosition, hotelName);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const recommendation = data[0];
  if (!recommendation) return null;

  const { recommendationId, campaignName, expiryDate, ownerName } =
    recommendation;
  const buyerLabel = ownerName || BUYER_FALLBACK_LABEL;
  const logo = getCampaignClientLogo(ownerName);

  const handleMaybeLater = () => {
    onDismiss?.();
  };

  const handleOptInNow = () => {
    setIsReviewOpen(true);
  };

  const handleReviewClose = () => {
    setIsReviewOpen(false);
  };

  const handleReviewActivate = () => {
    setIsReviewOpen(false);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    onDismiss?.();
  };

  return (
    <>
      <div ref={cardRef} className="campaign-card">
        <div className="campaign-card-header">
          <div className="campaign-card-badge">
            <img src={MegaphoneIcon} alt="" className="badge-icon" />
            <span>{BUYER_CAMPAIGN_BADGE}</span>
          </div>
          <div className="campaign-card-buyer-tag">
            {logo ? (
              <img
                src={logo}
                alt={buyerLabel}
                data-testid="campaign-card-buyer-logo"
                className="campaign-card-buyer-logo"
              />
            ) : (
              <span
                data-testid="campaign-card-buyer-name"
                className="campaign-card-buyer-name"
              >
                {buyerLabel}
              </span>
            )}
          </div>
        </div>

        <div className="campaign-card-text">
          <h3 className="campaign-card-title">{HERO_TITLE}</h3>
          <div className="campaign-card-paragraphs">
            <p className="campaign-card-description">
              {BODY_PREFIX}
              {buyerLabel}
              {campaignName && (
                <>
                  {" "}
                  <em className="campaign-card-promo-name">{campaignName}</em>
                </>
              )}
              {BODY_SUFFIX}
            </p>
            <p className="campaign-card-description">{BODY_TAIL}</p>
          </div>
        </div>

        {expiryDate && (
          <div className="campaign-card-expiry-section">
            <div className="campaign-card-expiry-banner">
              <img src={ClockIcon} alt="" className="expiry-icon" />
              <span className="expiry-text">
                {EXPIRY_PREFIX}
                {expiryDate}
                {EXPIRY_SUFFIX}
              </span>
            </div>
          </div>
        )}

        <div className="campaign-card-actions">
          <div className="campaign-card-actions-left">
            <Button
              id={`action-campaign-later-${recommendationId}`}
              appearance="link"
              className="maybe-later-btn"
              onClick={handleMaybeLater}
            >
              {MAYBE_LATER_LABEL}
            </Button>
          </div>
          <Button
            id={`action-campaign-view-${recommendationId}`}
            appearance="primary"
            className="opt-in-btn"
            onClick={handleOptInNow}
          >
            {LEARN_MORE_LABEL}{" "}
            <img src={ChevronRightWhiteIcon} alt="" className="btn-chevron" />
          </Button>
        </div>
      </div>

      {isReviewOpen && (
        <CampaignReviewModal
          data={recommendation}
          onClose={handleReviewClose}
          onActivate={handleReviewActivate}
        />
      )}

      {isFormOpen && (
        <CampaignActionForm
          recommendation={recommendation}
          selectedProperty={selectedProperty}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </>
  );
};

CampaignCard.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      recommendationId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
      ]).isRequired,
      campaignId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      propertyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      campaignName: PropTypes.string,
      description: PropTypes.string,
      ownerActor: PropTypes.string,
      ownerId: PropTypes.string,
      ownerName: PropTypes.string,
      promoObject: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      mediaIds: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      ),
      expiryDate: PropTypes.string,
      hasUsed: PropTypes.bool,
    })
  ).isRequired,
  onDismiss: PropTypes.func,
  selectedProperty: PropTypes.shape({
    propertyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    propertyName: PropTypes.string,
  }),
  cardPosition: PropTypes.number,
  hotelName: PropTypes.string,
};

export default CampaignCard;
